@if (!isAuthenticated) {
  <app-portal-navbar></app-portal-navbar>
}

<ion-content [fullscreen]="!isAuthenticated" [scrollEvents]="true">

  <!-- HERO -->
  <div class="hero" [class.hero--public]="!isAuthenticated" [class.hero--shell]="isAuthenticated">
    <div class="hero-grid-overlay"></div>
    <p class="eyebrow">{{ 'products.heroEyebrow' | translate }}</p>
    <h1>{{ 'products.heroTitleLine1' | translate }}<br><span class="gradient-text">{{ 'products.heroTitleLine2' | translate }}</span></h1>
    <p class="hero-sub">{{ 'products.heroSub' | translate }}</p>
  </div>

  <!-- PLATFORM (flagship) -->
  <div class="platform-section">
    <div class="platform-card">
      <div class="platform-card__header">
        <span class="platform-card__icon"><img src="assets/icon/portal-mark.png" alt="Gastro Management" /></span>
        <div class="platform-card__meta">
          <span class="platform-card__tag" [appTooltip]="'products.tt.portal' | translate"
          tooltipTheme="brand">{{ 'products.portalTag' | translate }}</span>
          <h2>Gastro Management</h2>
        </div>
        <ion-button shape="round" class="btn-product platform-card__cta" routerLink="/home">
          {{ 'products.toPortal' | translate }}
          <ion-icon name="arrow-forward-outline" slot="end"></ion-icon>
        </ion-button>
      </div>
      <p class="platform-card__desc">{{ 'products.portalDesc' | translate }}</p>
      <div class="platform-card__features">
        @for (f of portalFeatures; track f) {
          <div class="feature-item">
            <span class="feature-item__icon"><ion-icon [name]="f.icon"></ion-icon></span>
            <div>
              <strong>{{ f.title }}</strong>
              <span>{{ f.desc }}</span>
            </div>
          </div>
        }
      </div>
    </div>
  </div>

  <!-- ECOSYSTEM -->
  <div class="products-wrapper">
    <p class="section-eyebrow">{{ 'products.ecosystemEyebrow' | translate }}</p>
    <h2 class="section-title">{{ 'products.ecosystemTitle' | translate }}</h2>

    <div class="showcase-list">
      @for (p of showcases; track p; let i = $index) {
        <section class="showcase" [class.showcase--reverse]="i % 2 === 1">
          <!-- Visual: placeholder slider, swap for real screenshots per slide later -->
          <div class="showcase__visual">
            <div class="showcase-slider">
              @for (n of slideNumbers(p.slideCount); track n) {
                <div class="showcase-slide" [class.is-active]="slideOf(p.key) === n">
                  <ion-icon name="image-outline"></ion-icon>
                  <span>{{ 'products.showcase.placeholderLabel' | translate }}</span>
                  <span class="showcase-slide__count">{{ n + 1 }} / {{ p.slideCount }}</span>
                </div>
              }
              <button type="button" class="showcase-arrow is-prev" (click)="prevSlide(p.key, p.slideCount)" aria-label="Previous">
                <ion-icon name="chevron-back-outline"></ion-icon>
              </button>
              <button type="button" class="showcase-arrow is-next" (click)="nextSlide(p.key, p.slideCount)" aria-label="Next">
                <ion-icon name="chevron-forward-outline"></ion-icon>
              </button>
            </div>
            <div class="showcase-dots">
              @for (n of slideNumbers(p.slideCount); track n) {
                <span [class.is-active]="slideOf(p.key) === n" (click)="setSlide(p.key, n)"></span>
              }
            </div>
          </div>
          <!-- Content -->
          <div class="showcase__content">
            <div class="showcase__logo">
              @if (p.logoSrc) {
                <img [src]="p.logoSrc" [alt]="p.name" class="showcase__logo-img" />
              }
              @if (!p.logoSrc) {
                <span class="showcase__logo-generic">
                  <ion-icon [name]="p.logoIcon"></ion-icon>{{ p.name }}
                </span>
              }
            </div>
            <span class="card-tag" [appTooltip]="('products.tt.' + p.key) | translate">{{ p.tagKey | translate }}</span>
            <p class="showcase__desc">{{ p.descKey | translate }}</p>
            <ul class="showcase__features">
              @for (f of p.featureKeys; track f) {
                <li>
                  <ion-icon name="checkmark-circle-outline"></ion-icon>{{ f | translate }}
                </li>
              }
              @for (f of p.comingSoonFeatureKeys; track f) {
                <li class="is-coming-soon">
                  <ion-icon name="time-outline"></ion-icon>{{ f | translate }}
                  <span class="coming-soon-badge">{{ 'products.showcase.comingSoonBadge' | translate }}</span>
                </li>
              }
            </ul>
            @if (p.subItems) {
              <div class="showcase__sub-items">
                @for (s of p.subItems; track s) {
                  <div class="sub-item">
                    <strong>{{ s.titleKey | translate }}</strong>
                    <span>{{ s.descKey | translate }}</span>
                  </div>
                }
              </div>
            }
            @if (p.privacyKey) {
              <div class="showcase__privacy">
                <ion-icon name="shield-checkmark-outline"></ion-icon>
                <span>
                  {{ p.privacyKey | translate }}
                  <a [routerLink]="['/privacy']" [fragment]="p.privacyProduct">{{ 'products.showcase.privacyLinkText' | translate }}</a>
                </span>
              </div>
            }
            <div class="showcase__cta">
              @if (p.ctaRoute) {
                <ion-button shape="round" fill="outline" class="btn-product-outline" [routerLink]="p.ctaRoute">
                  {{ p.ctaLabelKey | translate }}
                  <ion-icon name="arrow-forward-outline" slot="end"></ion-icon>
                </ion-button>
              }
              @if (p.ctaExternalUrl) {
                <ion-button shape="round" fill="outline" class="btn-product-outline" [href]="p.ctaExternalUrl" target="_blank" rel="noopener">
                  {{ p.ctaLabelKey | translate }}
                  <ion-icon name="open-outline" slot="end"></ion-icon>
                </ion-button>
              }
            </div>
          </div>
        </section>
      }
    </div>
  </div>

  <!-- INTEGRATION BANNER -->
  <div class="integration-banner">
    <span class="banner-icon"><ion-icon name="link-outline" [appTooltip]="'products.tt.integration' | translate"></ion-icon></span>
    <div>
      <h3>{{ 'products.integrationTitle' | translate }}</h3>
      <p>{{ 'products.integrationDesc' | translate }}</p>
    </div>
    <ion-button shape="round" fill="outline" class="btn-banner" routerLink="/contact">
      {{ 'products.contactCta' | translate }}
    </ion-button>
  </div>

  <app-portal-footer></app-portal-footer>

</ion-content>
